// Copyright (c) 2026 Joshua S. Plaza. All rights reserved.

import React, { lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import { hideNotification } from './store/slices/uiSlice';

// Layouts
import MainLayout from './layouts/MainLayout';
import AuthLayout from './layouts/AuthLayout';
import PublicLayout from './layouts/PublicLayout';

// Lazy import retry utility
import retryLazy from './utils/retryLazy';

// Auth Pages
import Login from './pages/auth/Login';

// Auth Pages (lazy)
const ForgotPassword = lazy(() => retryLazy(() => import('./pages/auth/ForgotPassword')));
const ResetPassword = lazy(() => retryLazy(() => import('./pages/auth/ResetPassword')));

// Lazy-loaded pages
const AdminDashboard = lazy(() => retryLazy(() => import('./pages/admin/Dashboard')));
const UserManagement = lazy(() => retryLazy(() => import('./pages/admin/UserManagement')));
const ClassManagement = lazy(() => retryLazy(() => import('./pages/admin/ClassManagement')));
const SubjectManagement = lazy(() => retryLazy(() => import('./pages/admin/SubjectManagement')));
const SectionManagement = lazy(() => retryLazy(() => import('./pages/admin/SectionManagement')));
const EnrollmentManagement = lazy(() => retryLazy(() => import('./pages/admin/EnrollmentManagement')));
const SchoolYearManagement = lazy(() => retryLazy(() => import('./pages/admin/SchoolYearManagement')));
const AdminReportManagement = lazy(() => retryLazy(() => import('./pages/admin/ReportManagement')));
const BackupRestore = lazy(() => retryLazy(() => import('./pages/admin/BackupRestore')));
const AuditLog = lazy(() => retryLazy(() => import('./pages/admin/AuditLog')));
const SchoolHonorRoll = lazy(() => retryLazy(() => import('./pages/admin/SchoolHonorRoll')));

const TeacherDashboard = lazy(() => retryLazy(() => import('./pages/teacher/Dashboard')));
const TeacherClasses = lazy(() => retryLazy(() => import('./pages/teacher/MyClasses')));
const Gradebook = lazy(() => retryLazy(() => import('./pages/teacher/Gradebook')));
const AttendanceManagement = lazy(() => retryLazy(() => import('./pages/teacher/Attendance')));
const TeacherReports = lazy(() => retryLazy(() => import('./pages/teacher/Reports')));
const TeacherGuardians = lazy(() => retryLazy(() => import('./pages/teacher/Guardians')));
const TeacherAdvisory = lazy(() => retryLazy(() => import('./pages/teacher/Advisory')));

const StudentDashboard = lazy(() => retryLazy(() => import('./pages/student/Dashboard')));
const MyGrades = lazy(() => retryLazy(() => import('./pages/student/MyGrades')));
const MyAttendance = lazy(() => retryLazy(() => import('./pages/student/MyAttendance')));

const ParentDashboard = lazy(() => retryLazy(() => import('./pages/parent/Dashboard')));
const ChildProgress = lazy(() => retryLazy(() => import('./pages/parent/ChildProgress')));

const Announcements = lazy(() => retryLazy(() => import('./pages/shared/Announcements')));
const Profile = lazy(() => retryLazy(() => import('./pages/shared/Profile')));

const Landing = lazy(() => retryLazy(() => import('./pages/Landing')));
const AboutUs = lazy(() => retryLazy(() => import('./pages/public/AboutUs')));
const Reports = lazy(() => retryLazy(() => import('./pages/public/Reports')));
const CY2026 = lazy(() => retryLazy(() => import('./pages/public/CY2026')));
const CY2027 = lazy(() => retryLazy(() => import('./pages/public/CY2027')));
const CY2028 = lazy(() => retryLazy(() => import('./pages/public/CY2028')));
const UserGuide = lazy(() => retryLazy(() => import('./pages/public/UserGuide')));
const PrivacyPolicy = lazy(() => retryLazy(() => import('./pages/public/PrivacyPolicy')));
const TermsOfService = lazy(() => retryLazy(() => import('./pages/public/TermsOfService')));
const Enrollment = lazy(() => retryLazy(() => import('./pages/public/Enrollment')));

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return children;
};

// Role-based redirect
const RoleBasedRedirect = () => {
  const { user } = useSelector((state) => state.auth);

  switch (user?.role) {
    case 'ADMIN':
      return <Navigate to="/admin" replace />;
    case 'TEACHER':
      return <Navigate to="/teacher" replace />;
    case 'STUDENT':
      return <Navigate to="/student" replace />;
    case 'PARENT':
      return <Navigate to="/parent" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
};

function App() {
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { notification } = useSelector((state) => state.ui);

  const handleCloseNotification = () => {
    dispatch(hideNotification());
  };

  return (
    <>
      <Routes>
          {/* Auth Routes */}
          <Route element={<AuthLayout />}>
            <Route
              path="/login"
              element={isAuthenticated ? <RoleBasedRedirect /> : <Login />}
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:uidb64/:token" element={<ResetPassword />} />
          </Route>

          {/* Admin Routes */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allowedRoles={['ADMIN']}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="classes" element={<ClassManagement />} />
            <Route path="subjects" element={<SubjectManagement />} />
            <Route path="sections" element={<SectionManagement />} />
            <Route path="enrollments" element={<EnrollmentManagement />} />
            <Route path="school-years" element={<SchoolYearManagement />} />
            <Route path="reports" element={<AdminReportManagement />} />
            <Route path="backup" element={<BackupRestore />} />
            <Route path="honor-roll" element={<SchoolHonorRoll />} />
            <Route path="audit-logs" element={<AuditLog />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          {/* Teacher Routes */}
          <Route
            path="/teacher"
            element={
              <ProtectedRoute allowedRoles={['TEACHER']}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<TeacherDashboard />} />
            <Route path="classes" element={<TeacherClasses />} />
            <Route path="gradebook" element={<Gradebook />} />
            <Route path="gradebook/:classId" element={<Gradebook />} />
            <Route path="attendance" element={<AttendanceManagement />} />
            <Route path="attendance/:classId" element={<AttendanceManagement />} />
            <Route path="reports" element={<TeacherReports />} />
            <Route path="guardians" element={<TeacherGuardians />} />
            <Route path="advisory" element={<TeacherAdvisory />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          {/* Student Routes */}
          <Route
            path="/student"
            element={
              <ProtectedRoute allowedRoles={['STUDENT']}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<StudentDashboard />} />
            <Route path="grades" element={<MyGrades />} />
            <Route path="attendance" element={<MyAttendance />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          {/* Parent Routes */}
          <Route
            path="/parent"
            element={
              <ProtectedRoute allowedRoles={['PARENT']}>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<ParentDashboard />} />
            <Route path="child/:childId" element={<ChildProgress />} />
            <Route path="announcements" element={<Announcements />} />
            <Route path="profile" element={<Profile />} />
          </Route>

          {/* Public Routes (shared PublicLayout) */}
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Landing />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/cy-2026" element={<CY2026 />} />
            <Route path="/cy-2027" element={<CY2027 />} />
            <Route path="/cy-2028" element={<CY2028 />} />
            <Route path="/user-guide/:section?" element={<UserGuide />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route path="/enrollment" element={<Enrollment />} />
            <Route path="/reports" element={<Reports />} />
            <Route path="/reports/:category" element={<Reports />} />
          </Route>

          {/* Shared Routes */}
          <Route path="/unauthorized" element={<div>Unauthorized Access</div>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

      {/* Global Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default App;
